/** biome-ignore-all lint/suspicious/noEmptyBlockStatements: no empty blocks */
/** biome-ignore-all lint/style/useNamingConvention: camel case */

import cx from 'classnames';
import {useCallback, useEffect, useRef} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {
  usePostHostPhotosMutation,
  usePostPhotosMutation,
} from '~/ViewInvitation/api/endpoints/photos';
import {invalidateTag} from '~/ViewInvitation/api/photos';
import {usePhotoUpload} from '~/ViewInvitation/hooks/usePhotoUpload';
import {CLEAR_PENDING_PHOTOS, PHOTO_STATUS} from '~/ViewInvitation/reducers/constants';
import {
  selectCombinedPhotos,
  selectFeaturedPhoto,
  selectHostGalleryName,
} from '~/ViewInvitation/selectors/photos';
import {toastAlert} from '~library/atoms/Toast';
import {CheckmarkIcon} from '~library/svg/CheckmarkIcon';

const PARALLEL_PROCESSING_COUNT = 4;

export const usePostPhoto = ({
  setIsPosting = () => {},
  isPosting = false,
  closeGalleryEditor = () => {},
  isHostModule = false,
  galleryName = '',
  isCreationFlow = false,
}) => {
  const dispatch = useDispatch();
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const featuredPhoto = useSelector(selectFeaturedPhoto);
  const guestId = useSelector(({user}) => user?.guestId);
  const stagedGalleryName = useSelector(selectHostGalleryName);
  const combinedPhotos = useSelector(selectCombinedPhotos, shallowEqual);
  const expectedTotalRef = useRef(0);
  const hasPostedRef = useRef(false);
  const pendingPhotos = combinedPhotos?.filter((p) => p.status === PHOTO_STATUS.PENDING);
  const completedPhotos = combinedPhotos?.filter((p) => p.status === PHOTO_STATUS.SUCCESS);
  const failedPhotos = combinedPhotos?.filter((p) => p.status === PHOTO_STATUS.ERROR);
  const featuredId = featuredPhoto?.id;
  const collection = isHostModule ? 'hostPhotos' : 'photos';

  const isLargishAndUp = useIsLargishAndUp();

  const {doUploadPhoto} = usePhotoUpload({eventId, guestId, collection});
  const [postPhotos] = usePostPhotosMutation();
  const [postHostPhotos] = usePostHostPhotosMutation();

  // Warn user when trying to navigate away while posting
  useEffect(() => {
    if (isPosting) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
    return () => {};
  }, [isPosting]);

  const doPost = useCallback(async () => {
    try {
      if (isHostModule) {
        const photoUploads = completedPhotos.map((photo) => {
          const isFeatured = photo.id === featuredId;
          return {
            ...(isFeatured && photo.photoId && !photo.isStaged ? {id: photo.id} : {}),
            photo_id: photo.photoId,
            position: isFeatured ? 1 : undefined,
          };
        });
        await postHostPhotos({photos: photoUploads, title: stagedGalleryName, eventId});
      } else {
        const photoIds = completedPhotos.map((photo) => photo.photoId);
        await postPhotos({photos: photoIds, eventId, guestId});
      }
      closeGalleryEditor();
      dispatch({type: CLEAR_PENDING_PHOTOS});
      dispatch(invalidateTag(isHostModule));

      toastAlert('Photos added!', {
        className: cx('toast__processing-photo-success', {
          'toast__processing-photo-success-CF': isCreationFlow,
        }),
        type: 'success',
        icon: <CheckmarkIcon ratio={0.5} />,
        position: isLargishAndUp ? 'bottom-right' : 'top-right',
        autoClose: 3000,
        closeOnClick: true,
      });
      // TODO: Refactor the photo loading/posting/messaging state so we can avoid this. Its a safe timeout for the time being.
      setTimeout(() => {
        setIsPosting(false);
      }, 500);
    } catch (e) {
      setIsPosting(false);
      toastAlert(`Error uploading photos: ${e.message}`, {type: 'error'});
      dispatch({type: CLEAR_PENDING_PHOTOS});
      dispatch(invalidateTag(isHostModule));
    }
  }, [
    isHostModule,
    completedPhotos,
    featuredId,
    eventId,
    guestId,
    postPhotos,
    postHostPhotos,
    dispatch,
    setIsPosting,
    closeGalleryEditor,
    stagedGalleryName,
    isLargishAndUp,
  ]);

  const doPostPhotos = useCallback(async () => {
    if (!pendingPhotos.length && !featuredId) {
      dispatch({type: CLEAR_PENDING_PHOTOS});
      // Only gallery name was changed
      if (isHostModule && stagedGalleryName !== galleryName) {
        await postHostPhotos({photos: [], title: stagedGalleryName, eventId});
      }
      setIsPosting(false);
      closeGalleryEditor();
      return;
    }

    expectedTotalRef.current = pendingPhotos.length;
    hasPostedRef.current = false;
    setIsPosting(true);

    // Upload photos in batches of 4
    for (let i = 0; i < pendingPhotos.length; i += PARALLEL_PROCESSING_COUNT) {
      const chunk = pendingPhotos.slice(i, i + PARALLEL_PROCESSING_COUNT);
      await Promise.all(chunk.map((photo) => doUploadPhoto(photo)));
    }
  }, [
    dispatch,
    eventId,
    stagedGalleryName,
    galleryName,
    doUploadPhoto,
    setIsPosting,
    isHostModule,
    pendingPhotos,
    postHostPhotos,
    closeGalleryEditor,
    featuredId,
  ]);

  useEffect(() => {
    const completedCount = completedPhotos?.length || 0;
    const failedCount = failedPhotos?.length || 0;
    const expected = expectedTotalRef.current;

    if (
      expected > 0 &&
      !hasPostedRef.current &&
      pendingPhotos?.length === 0 &&
      completedCount + failedCount === expected
    ) {
      hasPostedRef.current = true;
      doPost();
    }
  }, [pendingPhotos, completedPhotos, failedPhotos, doPost]);

  const doFeatureOnly = useCallback(
    async (updates) => {
      if (!updates.length) return;
      try {
        await postHostPhotos({photos: updates, eventId});
        dispatch(invalidateTag(isHostModule));
      } catch (e) {
        toastAlert(`Error uploading photos: ${e.message}`, {type: 'error'});
        dispatch(invalidateTag(isHostModule));
        setIsPosting(false);
      }
    },
    [postHostPhotos, eventId, dispatch, isHostModule, setIsPosting]
  );

  return {doPostPhotos, doFeatureOnly};
};
